import { useState, useEffect, useRef, useCallback } from 'react';

// Global focus protection utility
export function useFocusProtection() {
  const protectedElementRef = useRef<HTMLElement | null>(null);
  const isProtectingRef = useRef<boolean>(false);

  const protectFocus = useCallback((element: HTMLElement | null) => {
    protectedElementRef.current = element;
    isProtectingRef.current = !!element;
    
    if (element) {
      console.log('🛡️ Focus protection activated for:', element.tagName);
    } else {
      console.log('🛡️ Focus protection deactivated');
    }
  }, []);

  const restoreFocusIfLost = useCallback(() => {
    if (isProtectingRef.current && protectedElementRef.current) {
      const activeElement = document.activeElement;
      if (activeElement !== protectedElementRef.current) {
        console.log('🔄 Restoring focus to protected element');
        protectedElementRef.current.focus();
        return true;
      }
    }
    return false;
  }, []);

  // Monitor for focus loss and restore if necessary
  useEffect(() => {
    if (!isProtectingRef.current) return;

    const checkFocus = () => {
      restoreFocusIfLost();
    };

    // Check periodically during protection
    const interval = setInterval(checkFocus, 100);
    
    return () => clearInterval(interval);
  }, [restoreFocusIfLost]);

  return {
    protectFocus,
    restoreFocusIfLost,
    isProtecting: isProtectingRef.current
  };
}

/**
 * Custom hook for debouncing a value
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced value
 */
export function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Custom hook for debouncing a callback function
 * @param callback - The callback function to debounce
 * @param delay - The delay in milliseconds
 * @param deps - Dependencies array for the callback
 * @returns The debounced callback function
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  deps: React.DependencyList = []
): T {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const callbackRef = useRef(callback);
  
  // Update callback ref when dependencies change
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...deps]);

  // Create stable debounced function using useCallback
  const debouncedFn = useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as T,
    [delay] // Only depend on delay, not on callback
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
}

/**
 * Search utilities for parsing search terms and handling quoted strings
 */
export class SearchUtils {
  /**
   * Parse search term to extract quoted strings and regular terms
   * @param searchTerm - The search term to parse
   * @returns Object with quoted terms and regular terms
   */
  static parseSearchTerm(searchTerm: string): {
    quotedTerms: string[];
    regularTerms: string[];
    originalTerm: string;
  } {
    const quotedTerms: string[] = [];
    const regularTerms: string[] = [];
    
    // Find all quoted strings (both single and double quotes)
    const quotedMatches = searchTerm.match(/["']([^"']*?)["']/g);
    if (quotedMatches) {
      quotedTerms.push(...quotedMatches.map(match => match.slice(1, -1).trim()));
    }
    
    // Remove quoted strings from the search term and get regular terms
    let remainingTerm = searchTerm.replace(/["']([^"']*?)["']/g, '');
    const words = remainingTerm.trim().split(/\s+/).filter(word => word.length > 0);
    regularTerms.push(...words);
    
    return {
      quotedTerms,
      regularTerms,
      originalTerm: searchTerm
    };
  }

  /**
   * Check if a value matches the search criteria
   * @param value - The value to search in
   * @param searchTerm - The search term (can include quoted strings)
   * @returns Boolean indicating if the value matches
   */
  static matchesSearch(value: any, searchTerm: string): boolean {
    if (!searchTerm) return true;
    if (value === null || value === undefined) return false;
    
    const stringValue = String(value).toLowerCase();
    const { quotedTerms, regularTerms } = this.parseSearchTerm(searchTerm.toLowerCase());
    
    // All quoted terms must match exactly (as substrings)
    const quotedMatches = quotedTerms.every(term => stringValue.includes(term));
    
    // All regular terms must match as substrings
    const regularMatches = regularTerms.every(term => stringValue.includes(term));
    
    return quotedMatches && regularMatches;
  }

  /**
   * Filter an array of objects based on search term
   * @param items - Array of items to filter
   * @param searchTerm - The search term
   * @param searchFields - Optional array of field names to search in, if not provided searches all string fields
   * @returns Filtered array
   */
  static filterItems<T>(
    items: T[], 
    searchTerm: string, 
    searchFields?: (keyof T)[]
  ): T[] {
    if (!searchTerm) return items;
    
    return items.filter(item => {
      if (searchFields && searchFields.length > 0) {
        // Search only in specified fields
        return searchFields.some(field => 
          this.matchesSearch(item[field], searchTerm)
        );
      } else {
        // Search in all fields
        return Object.values(item as any).some(value => 
          this.matchesSearch(value, searchTerm)
        );
      }
    });
  }
}